/**
 * `#intelUnderlying` — design §2: "View underlying indicators" toggles the source layers
 * (overlap COG, soil layers, the season rainfall and NDVI epochs, crop masks) and links the
 * survey indicators to the dashboard, so the path insight → indicator → catalogue entry
 * (design §5's traceability rule) exists for every driver.
 *
 * A `layer` entry toggles on the map exactly the way the two existing panes do: a raster
 * through `setActiveRaster` (R4's one-at-a-time raster, so ticking one may replace another —
 * the same rule the Layers pane's raster select already lives under), a vector through
 * `toggleLayer`. An `indicator` entry has nothing to toggle — it is a link to the dashboard.
 *
 * Fix round 1: every toggle also records itself in `intel.layersToggled`, so `#intelReset`
 * knows this is one of the ids it switched on and turns it back off — a layer the reader
 * had already switched on through the ordinary Layers pane is untouched by Reset.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { IntelUnderlying as IntelUnderlyingEntry } from '../../lib/api';
import { isRasterLayerId, selectActiveRaster, useAppStore } from '../../lib/state';

export function Underlying({ items }: { items: IntelUnderlyingEntry[] }) {
  const { t } = useTranslation();
  const activeRaster = useAppStore(selectActiveRaster);
  const layersOn = useAppStore((s) => s.layersOn);
  const setActiveRaster = useAppStore((s) => s.setActiveRaster);
  const toggleLayer = useAppStore((s) => s.toggleLayer);
  const setIntelLayerToggled = useAppStore((s) => s.setIntelLayerToggled);

  return (
    <div id="intelUnderlying" className="intel-underlying result-box">
      <h5>{t('intel.uc1.underlyingTitle')}</h5>
      <ul className="ad-list">
        {items.map((u) => {
          if (u.kind === 'indicator') {
            return (
              <li key={u.id}>
                <Link to={u.url}>{u.title}</Link>
              </li>
            );
          }
          const raster = isRasterLayerId(u.id);
          const on = raster ? activeRaster === u.id : layersOn.has(u.id);
          return (
            <li key={u.id}>
              <button
                type="button"
                className={on ? 'btn ghost sm on' : 'btn ghost sm'}
                data-testid={`intel-underlying-${u.id}`}
                aria-pressed={on}
                onClick={() => {
                  if (raster) setActiveRaster(on ? null : u.id);
                  else toggleLayer(u.id, !on);
                  setIntelLayerToggled(u.id, !on);
                }}
              >
                {u.title}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default Underlying;
